import { useEffect, useMemo, useRef, useState } from "react"
import { NodeViewContent, NodeViewWrapper } from "@tiptap/react"

import {
  getMermaidApi,
  initMermaid,
  registerMermaidContainer,
  sanitizeSource,
  unregisterMermaidContainer,
} from "@/lib/mermaid-renderer"
import { isValidPosition } from "@/tiptap/lib/tiptap-utils"
import { languageLabel } from "@/tiptap/nodes/code-block-node/language-labels"

const DEFAULT_LANGUAGE = "plaintext"
const MERMAID_LANGUAGE = "mermaid"

const TYPING_IDLE_MS = 800

/** True only while the user is actively keying inside this code block. */
function useIsTypingInCodeBlock(editor, getPos) {
  const [isTyping, setIsTyping] = useState(false)
  const idleTimerRef = useRef(null)

  useEffect(() => {
    if (!editor || typeof getPos !== "function") return undefined

    const dom = editor.view?.dom
    if (!dom) return undefined

    const clearIdleTimer = () => {
      if (idleTimerRef.current != null) {
        clearTimeout(idleTimerRef.current)
        idleTimerRef.current = null
      }
    }

    const isCaretInBlock = () => {
      try {
        const pos = getPos()
        if (typeof pos !== "number") return false

        const currentNode = editor.state.doc.nodeAt(pos)
        if (!currentNode) return false

        const { from } = editor.state.selection
        return editor.isFocused && from > pos && from < pos + currentNode.nodeSize
      } catch {
        return false
      }
    }

    const markTyping = () => {
      if (!isCaretInBlock()) return

      setIsTyping(true)
      clearIdleTimer()
      idleTimerRef.current = setTimeout(() => {
        setIsTyping(false)
        idleTimerRef.current = null
      }, TYPING_IDLE_MS)
    }

    const stopTyping = () => {
      clearIdleTimer()
      setIsTyping(false)
    }

    const onSelectionUpdate = () => {
      if (!isCaretInBlock()) stopTyping()
    }

    dom.addEventListener("keydown", markTyping)
    dom.addEventListener("beforeinput", markTyping)
    editor.on("blur", stopTyping)
    editor.on("selectionUpdate", onSelectionUpdate)

    return () => {
      clearIdleTimer()
      dom.removeEventListener("keydown", markTyping)
      dom.removeEventListener("beforeinput", markTyping)
      editor.off("blur", stopTyping)
      editor.off("selectionUpdate", onSelectionUpdate)
    }
  }, [editor, getPos])

  return isTyping
}

// Survive React node-view remounts (common on mobile IME Enter). Without this,
// mode resets to "preview" whenever hasContent, hiding the source and stealing
// focus so every newline looks like "exiting" the mermaid block.
const mermaidModeByNodeId = new Map()

function isSelectionInsideCodeBlock(editor, getPos, nodeSize) {
  if (!editor || typeof getPos !== "function" || !nodeSize) return false

  const pos = getPos()
  if (!isValidPosition(pos)) return false

  const { from, to } = editor.state.selection
  return from > pos && to < pos + nodeSize
}

function initialMermaidMode({ editor, getPos, node, hasContent }) {
  const nodeId = node.attrs?.id
  if (nodeId && mermaidModeByNodeId.has(nodeId)) {
    return mermaidModeByNodeId.get(nodeId)
  }

  if (isSelectionInsideCodeBlock(editor, getPos, node.nodeSize)) {
    return "edit"
  }

  return hasContent ? "preview" : "edit"
}

function MermaidPreview({ source }) {
  const containerRef = useRef(null)

  useEffect(() => {
    const el = containerRef.current
    if (!el) return undefined

    registerMermaidContainer(el)
    return () => unregisterMermaidContainer(el)
  }, [])

  useEffect(() => {
    let cancelled = false
    const trimmed = sanitizeSource(source || "")
    const target = containerRef.current
    if (!target) return undefined

    if (!trimmed) {
      target.innerHTML = ""
      target.classList.remove("code-block-mermaid-preview--error")
      return undefined
    }

    async function render() {
      try {
        initMermaid()
      } catch (err) {
        if (cancelled || !containerRef.current) return
        containerRef.current.classList.add("code-block-mermaid-preview--error")
        containerRef.current.textContent =
          "Diagram error: " + (err.message || "Mermaid not available")
        return
      }

      const api = getMermaidApi()
      if (!api) {
        if (cancelled || !containerRef.current) return
        containerRef.current.classList.add("code-block-mermaid-preview--error")
        containerRef.current.textContent = "Diagram error: Mermaid not available"
        return
      }

      const id = "mermaid-tt-" + Math.random().toString(36).slice(2, 10)

      try {
        const { svg } = await api.render(id, trimmed)
        if (cancelled || !containerRef.current) return
        containerRef.current.classList.remove("code-block-mermaid-preview--error")
        containerRef.current.innerHTML = svg
        containerRef.current.dataset.mermaidSource = trimmed
      } catch (err) {
        const orphan = document.getElementById("d" + id)
        if (orphan) orphan.remove()
        if (cancelled || !containerRef.current) return
        containerRef.current.classList.add("code-block-mermaid-preview--error")
        containerRef.current.textContent =
          "Diagram error: " + (err.message || "Invalid syntax")
      }
    }

    render()

    return () => {
      cancelled = true
    }
  }, [source])

  return (
    <div
      contentEditable={false}
      className="code-block-mermaid-preview-wrapper">
      <div ref={containerRef} className="code-block-mermaid-preview" />
    </div>
  )
}

function MermaidNodeView({
  editor,
  getPos,
  node,
  languages,
  currentLanguage,
  handleLanguageChange,
  codeClassName,
  isTyping = false,
}) {
  const sourceText = node.textContent || ""
  const hasContent = sourceText.trim().length > 0
  const nodeId = node.attrs?.id

  const [mode, setMode] = useState(() =>
    initialMermaidMode({ editor, getPos, node, hasContent })
  )

  useEffect(() => {
    if (nodeId) mermaidModeByNodeId.set(nodeId, mode)
  }, [nodeId, mode])

  useEffect(() => {
    if (mode === "preview" && !hasContent) {
      setMode("edit")
    }
  }, [hasContent, mode])

  const isPreview = mode === "preview"
  // Same hide-on-type behavior as plaintext code blocks (language picker).
  const wrapperClassName = [
    "code-block-wrapper",
    "code-block-mermaid",
    !isPreview && isTyping ? "code-block-wrapper--typing" : null,
  ]
    .filter(Boolean)
    .join(" ")

  const toggleMode = () => {
    setMode((current) => (current === "preview" ? "edit" : "preview"))
  }

  return (
    <NodeViewWrapper className={wrapperClassName}>
      <div contentEditable={false} className="code-block-mermaid-toolbar">
        <button
          type="button"
          className="code-block-mermaid-toggle"
          onClick={toggleMode}
          onMouseDown={(event) => event.preventDefault()}
          disabled={!isPreview && !hasContent}>
          {isPreview ? "Edit" : "Preview"}
        </button>
        <select
          className="code-block-language-selector"
          value={currentLanguage}
          onChange={handleLanguageChange}
          aria-label="Code language">
          {languages.map((language) => (
            <option key={language} value={language}>
              {languageLabel(language)}
            </option>
          ))}
        </select>
      </div>

      {isPreview ? <MermaidPreview source={sourceText} /> : null}

      <pre
        className={
          isPreview
            ? "code-block-mermaid-source code-block-mermaid-source--hidden"
            : "code-block-mermaid-source"
        }>
        <NodeViewContent as="code" className={codeClassName} />
      </pre>
    </NodeViewWrapper>
  )
}

export function CodeBlockNodeView({
  extension,
  node,
  updateAttributes,
  editor,
  getPos,
}) {
  const lowlightLanguages = useMemo(() => {
    const lowlight = extension.options.lowlight
    return typeof lowlight?.listLanguages === "function"
      ? lowlight.listLanguages()
      : []
  }, [extension])

  const languages = useMemo(() => {
    return [...new Set([MERMAID_LANGUAGE, ...lowlightLanguages])].sort((a, b) =>
      languageLabel(a).localeCompare(languageLabel(b))
    )
  }, [lowlightLanguages])

  const currentLanguage = node.attrs.language || DEFAULT_LANGUAGE
  const isTyping = useIsTypingInCodeBlock(editor, getPos)

  const handleLanguageChange = (event) => {
    const value = event.target.value
    updateAttributes({
      language: value || DEFAULT_LANGUAGE,
    })
  }

  const codeClassName = [
    "hljs",
    node.attrs.language ? `language-${node.attrs.language}` : null,
  ]
    .filter(Boolean)
    .join(" ")

  const wrapperClassName = [
    "code-block-wrapper",
    isTyping ? "code-block-wrapper--typing" : null,
  ]
    .filter(Boolean)
    .join(" ")

  if (currentLanguage === MERMAID_LANGUAGE) {
    return (
      <MermaidNodeView
        editor={editor}
        getPos={getPos}
        node={node}
        languages={languages}
        currentLanguage={currentLanguage}
        handleLanguageChange={handleLanguageChange}
        codeClassName={codeClassName}
        isTyping={isTyping}
      />
    )
  }

  return (
    <NodeViewWrapper className={wrapperClassName}>
      <select
        contentEditable={false}
        className="code-block-language-selector"
        value={currentLanguage}
        onChange={handleLanguageChange}
        aria-label="Code language">
        {languages.map((language) => (
          <option key={language} value={language}>
            {languageLabel(language)}
          </option>
        ))}
      </select>
      <pre>
        <NodeViewContent as="code" className={codeClassName} />
      </pre>
    </NodeViewWrapper>
  )
}

export default CodeBlockNodeView
